<script setup lang="ts">

import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { BtnLink, LinkTextPrimary } from '@shared/ui/links'
import { BtnCheckbox } from '@shared/ui/buttons'
import { AppBadge } from '@shared/ui/badges'

import LessonActions from './LessonActions.vue'
import LessonStatus from './LessonStatus.vue'
import LessonDate from './LessonDate.vue'
import { useTableLessons } from '../../model/lessons/table-lessons.hook'

defineOptions({
  name: 'TableLessons',
})

const COLUMNS: readonly { title: string, className: string }[] = [
  { title: '#', className: 'table-lessons__id' },
  { title: 'Date & time', className: 'table-lessons__date' },
  { title: 'Type', className: 'table-lessons__type' },
  { title: 'Status', className: 'table-lessons__status' },
  { title: 'Tutor', className: 'table-lessons__tutor' },
  { title: 'Task', className: '' },
  { title: 'Screen recording', className: 'table-lessons__recording' },
]

const {
  rows,
  isLoading,
  hasLessons,
  meta,
  perPageOptions,
  refCanManage,
  refIsAllSelected,
  isSelected,
  toggleSelected,
  toggleAllSelected,
  setChangePage,
  setChangePerPage,
} = useTableLessons()
</script>

<template>
  <div class="table-lessons">
    <AppTable
      class="rounded-table"
      :meta="meta"
      :per-page-options="perPageOptions"
      :loading="isLoading"
      @change:page="setChangePage"
      @change:per-page="setChangePerPage"
    >
      <template #head>
        <AppTr>
          <AppTh
            v-if="refCanManage"
            class="table-lessons__check"
          >
            <BtnCheckbox
              :checked="refIsAllSelected"
              :disabled="!hasLessons"
              @click="toggleAllSelected"
            />
          </AppTh>
          <AppTh
            v-for="column in COLUMNS"
            :key="column.title"
            :class="column.className"
          >
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              {{ column.title }}
            </AppText>
          </AppTh>
          <AppTh
            v-if="refCanManage"
            class="table-lessons__actions"
          />
        </AppTr>
      </template>

      <template
        v-if="hasLessons"
        #body
      >
        <AppTr
          v-for="row in rows"
          :key="row.lesson.id"
        >
          <AppTd
            v-if="refCanManage"
            class="medium table-lessons__check"
          >
            <BtnCheckbox
              :checked="isSelected(row.lesson.id)"
              @click="toggleSelected(row.lesson.id)"
            />
          </AppTd>
          <AppTd class="medium table-lessons__id">
            <LinkTextPrimary :href="row.editUrl">
              {{ row.lesson.id }}
            </LinkTextPrimary>
          </AppTd>
          <AppTd class="medium table-lessons__date">
            <LessonDate
              :lesson="row.lesson"
              :start="row.start"
              :edit-url="row.editUrl"
            />
          </AppTd>
          <AppTd class="medium table-lessons__type">
            <AppBadge
              :color="row.typeColor"
              :text="row.typeLabel"
            />
          </AppTd>
          <AppTd class="medium table-lessons__status">
            <LessonStatus
              :lesson="row.lesson"
              :note="row.statusNote"
            />
          </AppTd>
          <AppTd class="medium table-lessons__tutor">
            <LinkTextPrimary
              v-if="row.tutorUrl"
              :href="row.tutorUrl"
            >
              {{ row.tutorName }}
            </LinkTextPrimary>
            <AppText
              v-else
              name="body1"
              color="red-600"
            >
              Unassigned tutor
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <LinkTextPrimary
              v-if="row.tasksUrl"
              :href="row.tasksUrl"
            >
              {{ row.tasksText }}
            </LinkTextPrimary>
            <AppText
              v-else
              name="body1"
            >
              {{ row.tasksText }}
            </AppText>
          </AppTd>
          <AppTd class="medium table-lessons__recording">
            <BtnLink
              v-if="row.lesson.recording_url"
              :link-url="row.lesson.recording_url"
              link-name="Open"
            />
            <AppText
              v-else
              name="body1"
            >
              -
            </AppText>
          </AppTd>
          <AppTd
            v-if="refCanManage"
            class="zero-padding table-lessons__actions"
          >
            <LessonActions
              :lesson="row.lesson"
              :edit-url="row.editUrl"
            />
          </AppTd>
        </AppTr>
      </template>
    </AppTable>
  </div>
</template>

<style scoped lang="scss" src="./TableLessons.scss" />
